import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
    DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
    useDraggable, useDroppable, closestCenter,
} from "@dnd-kit/core";
import { api, apiErr } from "../../api/client";
import { ADMIN } from "../../constants/testIds";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { InitialsAvatar } from "../../components/shared/InitialsAvatar";
import { StatusBadge } from "../../components/shared/StatusBadge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
    ChevronLeft, ChevronRight, Send, MapPin, Trash2, Grip, Search, X, Radio,
} from "lucide-react";

const DAY_LABELS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

function mondayOf(d) {
    const dt = new Date(d);
    const dow = (dt.getDay() + 6) % 7;
    dt.setDate(dt.getDate() - dow);
    dt.setHours(0, 0, 0, 0);
    return dt;
}
function toIsoDate(d) { return d.toISOString().slice(0, 10); }
function fmtRange(monday) {
    const sun = new Date(monday); sun.setDate(sun.getDate() + 6);
    const opts = { day: "numeric", month: "short" };
    return `${monday.toLocaleDateString("en-AU", opts)} – ${sun.toLocaleDateString("en-AU", { ...opts, year: "numeric" })}`;
}

export default function Schedule() {
    const [weekMon, setWeekMon] = useState(toIsoDate(mondayOf(new Date())));
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [activeDrag, setActiveDrag] = useState(null); // { kind, payload }
    const [jobQuery, setJobQuery] = useState("");
    const [hoursDialog, setHoursDialog] = useState(null);
    const monday = useMemo(() => new Date(weekMon + "T00:00"), [weekMon]);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    const load = useCallback(async () => {
        const { data } = await api.get("/v1/schedule/week", { params: { week: weekMon } });
        setData(data);
    }, [weekMon]);

    useEffect(() => { load(); }, [load]);

    if (!data) return <div className="p-8 text-muted">Loading schedule…</div>;

    const shift = (n) => {
        const d = new Date(monday); d.setDate(d.getDate() + n * 7);
        setWeekMon(toIsoDate(d));
    };

    // Build cell map for O(1) lookup: users × days -> assignments[]
    const cellMap = {};
    for (const a of data.assignments) {
        const k = `${a.user_id}::${a.date}`;
        (cellMap[k] ||= []).push(a);
    }
    const hasUnpublished = data.assignments.some((a) => !a.published);

    const jobPoolFiltered = data.job_pool.filter((j) => {
        if (!jobQuery.trim()) return true;
        const q = jobQuery.toLowerCase();
        return (
            (j.job_number || "").toLowerCase().includes(q) ||
            (j.address || "").toLowerCase().includes(q)
        );
    });

    const onDragStart = (e) => {
        const id = e.active.id;
        const [kind, ...rest] = id.split("::");
        if (kind === "job") {
            const jobId = rest.join("::");
            const job = data.job_pool.find((j) => j.id === jobId);
            setActiveDrag({ kind: "job", job });
        } else if (kind === "asg") {
            const asgId = rest.join("::");
            const asg = data.assignments.find((a) => a.id === asgId);
            setActiveDrag({ kind: "asg", asg });
        }
    };

    const onDragEnd = async (e) => {
        const drag = activeDrag;
        setActiveDrag(null);
        if (!drag || !e.over) return;
        const overId = e.over.id;
        if (!overId.startsWith("cell::")) return;
        const [_, userId, date] = overId.split("::");

        try {
            if (drag.kind === "job") {
                await api.post("/v1/schedule/assignments", {
                    user_id: userId, date, job_id: drag.job.id, hours: 8.0,
                });
            } else if (drag.kind === "asg") {
                if (drag.asg.user_id === userId && drag.asg.date === date) return;
                await api.patch(`/v1/schedule/assignments/${drag.asg.id}`, {
                    user_id: userId, date,
                });
            }
            await load();
        } catch (err) { toast.error(apiErr(err)); }
    };

    const deleteAsg = async (asg) => {
        try {
            await api.delete(`/v1/schedule/assignments/${asg.id}`);
            await load();
        } catch (e) { toast.error(apiErr(e)); }
    };

    const updateHours = async (asg, hours) => {
        try {
            await api.patch(`/v1/schedule/assignments/${asg.id}`, { hours: parseFloat(hours) || 0 });
            await load();
            setHoursDialog(null);
        } catch (e) { toast.error(apiErr(e)); }
    };

    const publish = async () => {
        setBusy(true);
        try {
            const { data: res } = await api.post("/v1/schedule/publish", { week: weekMon });
            const n = res.affected_users?.length || 0;
            toast.success(
                n === 0
                    ? "Nothing new to publish"
                    : `Published · ${res.assignments_touched} changes · ${n} staff notified · ${res.push_delivered} pushes delivered`,
            );
            await load();
        } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };

    return (
        <div data-testid={ADMIN.scheduleRoot} className="space-y-6">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="eyebrow">Admin</div>
                    <h1 className="text-4xl font-extrabold tracking-[-0.03em] leading-none">Schedule</h1>
                    <p className="text-muted mt-2 text-sm">
                        Drag jobs from the pool onto a staff × day cell. Click a card to change hours or remove.
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1">
                        <Button data-testid={ADMIN.schedulePrev} variant="outline" size="sm" onClick={() => shift(-1)}>
                            <ChevronLeft size={16} />
                        </Button>
                        <div data-testid={ADMIN.scheduleWeekLabel} className="num text-sm px-3 min-w-[200px] text-center">
                            {fmtRange(monday)}
                        </div>
                        <Button data-testid={ADMIN.scheduleNext} variant="outline" size="sm" onClick={() => shift(1)}>
                            <ChevronRight size={16} />
                        </Button>
                    </div>
                    <Button
                        data-testid={ADMIN.schedulePublish}
                        onClick={publish}
                        disabled={busy || !hasUnpublished}
                        className="gap-2 bg-[color:var(--km-green)] hover:bg-[color:var(--km-green-d)]"
                    >
                        <Send size={14} /> Publish
                        {hasUnpublished && (
                            <span className="km-chip km-chip-yellow ml-1 text-[10px] py-0 px-1.5">
                                unpublished
                            </span>
                        )}
                    </Button>
                </div>
            </div>

            <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} collisionDetection={closestCenter}>
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
                    {/* Schedule grid */}
                    <div className="km-card overflow-x-auto" data-testid={ADMIN.scheduleGrid}>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] uppercase tracking-[0.1em] text-muted border-b border-km">
                                    <th className="text-left px-4 py-3 font-medium min-w-[180px]">Staff</th>
                                    {data.days.map((d, i) => (
                                        <th key={d} className="px-2 py-3 font-medium min-w-[140px]">
                                            <div className="text-left">{DAY_LABELS[i]}</div>
                                            <div className="num text-[10px] text-muted mt-0.5">{d.slice(5)}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.users.map((u) => (
                                    <tr key={u.id} className="border-b border-km align-top">
                                        <td className="px-4 py-3 sticky left-0 bg-[color:var(--surface-0)]">
                                            <div className="flex items-center gap-3">
                                                <InitialsAvatar name={u.name} initials={u.initials} size={30} />
                                                <div>
                                                    <div className="text-ink font-medium text-sm">{u.name}</div>
                                                    <div className="text-[10px] uppercase tracking-wider text-muted">{u.role}</div>
                                                </div>
                                            </div>
                                        </td>
                                        {data.days.map((d) => {
                                            const key = `${u.id}::${d}`;
                                            const cards = cellMap[key] || [];
                                            return (
                                                <ScheduleCell
                                                    key={d}
                                                    testid={ADMIN.scheduleCell(u.id, d)}
                                                    userId={u.id}
                                                    date={d}
                                                    cards={cards}
                                                    onEditHours={(a) => setHoursDialog(a)}
                                                    onDelete={deleteAsg}
                                                />
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Job pool */}
                    <div className="km-card p-4" data-testid={ADMIN.jobPool}>
                        <div className="flex items-center gap-2 mb-3">
                            <MapPin size={14} className="text-accent" />
                            <div className="text-xs uppercase tracking-[0.14em] text-muted font-medium">
                                Job pool · {jobPoolFiltered.length}
                            </div>
                        </div>
                        <div className="relative mb-3">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                            <Input
                                value={jobQuery}
                                onChange={(e) => setJobQuery(e.target.value)}
                                placeholder="Search"
                                className="pl-9 h-8 text-sm"
                            />
                        </div>
                        <div className="space-y-1.5 max-h-[560px] overflow-y-auto pr-1">
                            {jobPoolFiltered.map((j) => (
                                <JobPoolCard key={j.id} job={j} />
                            ))}
                            {!jobPoolFiltered.length && (
                                <div className="text-xs text-muted py-6 text-center">No matching jobs.</div>
                            )}
                        </div>
                    </div>
                </div>

                <DragOverlay dropAnimation={null}>
                    {activeDrag?.kind === "job" && <JobPoolCard job={activeDrag.job} overlay />}
                    {activeDrag?.kind === "asg" && <AsgCardBody job={activeDrag.asg.job} hours={activeDrag.asg.hours} overlay />}
                </DragOverlay>
            </DndContext>

            {/* Hours editor */}
            <Dialog open={!!hoursDialog} onOpenChange={(o) => !o && setHoursDialog(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-extrabold tracking-[-0.03em]">Assignment</DialogTitle>
                    </DialogHeader>
                    {hoursDialog && (
                        <div className="space-y-4">
                            <div className="text-sm">
                                <div className="num font-bold text-[color:var(--km-navy)]">{hoursDialog.job?.job_number || "—"}</div>
                                <div className="text-muted">{hoursDialog.job?.address}</div>
                                <div className="text-[11px] uppercase tracking-wider text-muted mt-1 num">{hoursDialog.date}</div>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.12em] text-muted">Hours</label>
                                <Input
                                    type="number" step="0.5" min="0" max="24"
                                    defaultValue={hoursDialog.hours ?? 8}
                                    className="num"
                                    id="hours-input"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter className="justify-between">
                        <Button
                            variant="outline"
                            onClick={() => { deleteAsg(hoursDialog); setHoursDialog(null); }}
                            className="text-[color:var(--km-red)] border-[color:var(--km-red-l)] gap-2"
                        >
                            <Trash2 size={14} /> Remove
                        </Button>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setHoursDialog(null)}>Cancel</Button>
                            <Button onClick={() => {
                                const v = document.getElementById("hours-input").value;
                                updateHours(hoursDialog, v);
                            }}>Save</Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function JobPoolCard({ job, overlay }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `job::${job.id}`,
        data: { kind: "job", job },
    });
    const cls = `border rounded-lg px-3 py-2 bg-white cursor-grab active:cursor-grabbing transition ${
        overlay ? "shadow-lg" : ""
    } ${isDragging ? "opacity-30" : "hover:border-[color:var(--km-navy)]"}`;
    return (
        <div ref={setNodeRef} {...attributes} {...listeners} className={cls} style={{ borderColor: "var(--line-1)" }}>
            <div className="flex items-center gap-2">
                <Grip size={12} className="text-muted shrink-0" />
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="num text-xs font-bold text-[color:var(--km-navy)]">{job.job_number || "—"}</span>
                        <StatusBadge status={job.status} className="text-[10px] py-0 px-1.5" />
                    </div>
                    <div className="text-xs text-ink truncate">{job.address}</div>
                </div>
            </div>
        </div>
    );
}

function ScheduleCell({ testid, userId, date, cards, onEditHours, onDelete }) {
    const { setNodeRef, isOver } = useDroppable({
        id: `cell::${userId}::${date}`,
    });
    const dow = new Date(date + "T00:00").getDay(); // 0=Sun, 6=Sat
    const isWeekend = dow === 0 || dow === 6;
    return (
        <td
            ref={setNodeRef}
            data-testid={testid}
            className="px-1.5 py-2 border-l border-km align-top"
            style={{
                background: isOver
                    ? "var(--km-navy-l)"
                    : isWeekend
                        ? "var(--surface-2)"
                        : undefined,
                minHeight: 96,
            }}
        >
            <div className="space-y-1 min-h-[80px]">
                {cards.map((a) => (
                    <AsgCard key={a.id} asg={a} onClick={() => onEditHours(a)} />
                ))}
            </div>
        </td>
    );
}

function AsgCard({ asg, onClick }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `asg::${asg.id}`,
        data: { kind: "asg", asg },
    });
    const cls = `rounded-md px-2 py-1.5 text-xs cursor-grab active:cursor-grabbing transition ${
        isDragging ? "opacity-30" : ""
    }`;
    return (
        <div
            ref={setNodeRef}
            {...attributes} {...listeners}
            onClick={onClick}
            className={cls}
            data-testid={`sched-card-${asg.id}`}
            style={{
                background: asg.published ? "var(--km-navy-l)" : "var(--km-yellow-l)",
                border: `1px solid ${asg.published ? "var(--km-navy)" : "var(--km-yellow-d)"}`,
                color: "var(--ink-1)",
            }}
        >
            <AsgCardBody job={asg.job} hours={asg.hours} published={asg.published} />
        </div>
    );
}

function AsgCardBody({ job, hours, published, overlay }) {
    return (
        <div className={overlay ? "px-2 py-1.5 rounded-md bg-white shadow-lg border" : ""}>
            <div className="flex items-center justify-between gap-1">
                <span className="num text-[11px] font-bold text-[color:var(--km-navy)]">
                    {job?.job_number || "—"}
                </span>
                <span className="num text-[11px] text-ink">{Number(hours || 0).toFixed(1)}h</span>
            </div>
            <div className="text-[11px] truncate mt-0.5" title={job?.address}>{job?.address || "—"}</div>
            {published === false && (
                <div className="text-[9px] uppercase tracking-wider text-[color:var(--km-yellow-d)] font-bold mt-0.5">
                    Unpublished
                </div>
            )}
        </div>
    );
}
